"use client";

import { addNextjsError } from "@datadog/browser-rum-nextjs";
import { useEffect } from "react";
import type { ErrorInfo } from "react";
import {
  type NextjsReportableError,
  reportNextjsErrorToRum,
} from "./reportNextjsErrorToRum";

export type NextjsAppRouterError = NextjsReportableError;

export { __resetReportedNextjsErrorsForTests } from "./reportNextjsErrorToRum";

/**
 * Reports a Next.js App Router error page error to Datadog RUM.
 *
 * Intended for `app/error.tsx` and `app/global-error.tsx` components.
 * Each distinct error (by digest or message/first stack frame) is reported at most once
 * within a short window to absorb React Strict Mode remounts, while still
 * allowing a fresh report after the user clicks reset.
 *
 * Next.js only passes `error` and `reset` to error boundaries — not React's
 * `errorInfo` (component stack). Callers should use `useReportNextjsError(error)`
 * without `errorInfo`; the optional second argument exists for `addNextjsError`
 * compatibility if a future Next.js version exposes it.
 */
export function useReportNextjsError(
  error: NextjsAppRouterError,
  errorInfo?: ErrorInfo,
): void {
  useEffect(() => {
    reportNextjsErrorToRum(error, addNextjsError, errorInfo);
  }, [error, errorInfo]);
}
